/**
 * İÇERİK ÇEVİRİSİ — seçilen sayfayı hedef dile YERELLEŞTİRİR (birebir çeviri değil):
 * hedef pazarda gerçekten aranan karşılık kelimeler (DataForSEO hacim/KD/CPC doğrulamalı)
 * hedeflenir, iç linkler hedef dildeki karşılık sayfalara eşlenir, GEO/SEO kuralları uygulanır.
 */
import { confirmCost } from "@/components/cost-confirm";
import { useState, useMemo, useRef, useEffect } from "react";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Globe, Loader2, Languages, Search } from "lucide-react";
import { Link } from "wouter";
import { AgentOption } from "@/components/agent-ui";
import { useToast } from "@/hooks/use-toast";

type KeywordEquivalent = { source: string; candidates: { keyword: string; volume: number; difficulty: number; cpc: number }[]; chosen: string };
type TranslationData = {
  pageId: number; sourceUrl: string; sourceTitle: string; sourceLang: string | null; targetLang: string;
  keywordMap: KeywordEquivalent[];
  semanticKeywords: { term: string; volume: number }[];
  translatedTitle: string; translatedMeta: string; translatedContent: string;
  linkMapping: { anchor: string; sourceUrl: string; targetUrl: string | null; note: string; context?: string }[];
  geoNotes: string[]; clusterName: string | null; cost: number;
  sourceKind?: "canli" | "revize";
  completeness?: { sourceHeadings: number; translatedHeadings: number; sourceChars: number; translatedChars: number };
};
type PageItem = { id: number; url: string; title: string | null; primaryKeyword: string | null; language?: string | null };
type TranslationListItem = {
  id: number; pageId: number; targetLang: string; runAt: string;
  sourceTitle: string; sourceUrl: string; sourceLang: string | null;
  translatedTitle: string; clusterName: string | null; contentLength: number;
  keywords: { source: string; chosen: string; volume: number }[];
  linksMapped: number; linksRemoved: number; geoNoteCount: number;
  thumb?: string | null;
  completeness?: { sourceHeadings: number; translatedHeadings: number } | null;
  score?: number | null;
  wordCount?: number | null;
};

const C = { cyan: "#e83e3c", green: "#12b76a", red: "#f04438", amber: "#f79009", purple: "#e83e3c" };
const fmt = (n: number) => (n >= 10000 ? `${(n / 1000).toFixed(0)}k` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(Math.round(n)));
const shortUrl = (u: string) => { try { const p = new URL(u); return p.pathname.replace(/\/$/, "") || "/"; } catch { return u; } };

// Hedef diller (backend LOCALES ile eşlenik; "auto" yok — çeviri hedefi net seçilir)
const TARGET_LANGS: { k: string; l: string }[] = [
  { k: "de", l: "🇩🇪 Almanca" }, { k: "en", l: "🇺🇸 İngilizce (US)" }, { k: "en-gb", l: "🇬🇧 İngilizce (UK)" },
  { k: "tr", l: "🇹🇷 Türkçe" }, { k: "ar", l: "🇸🇦 Arapça" }, { k: "fr", l: "🇫🇷 Fransızca" },
  { k: "es", l: "🇪🇸 İspanyolca" }, { k: "it", l: "🇮🇹 İtalyanca" }, { k: "ru", l: "🇷🇺 Rusça" },
  { k: "nl", l: "🇳🇱 Felemenkçe" }, { k: "pt", l: "🇵🇹 Portekizce" }, { k: "pt-br", l: "🇧🇷 Portekizce (BR)" },
  { k: "pl", l: "🇵🇱 Lehçe" }, { k: "sv", l: "🇸🇪 İsveççe" }, { k: "el", l: "🇬🇷 Yunanca" },
  { k: "he", l: "🇮🇱 İbranice" }, { k: "ja", l: "🇯🇵 Japonca" }, { k: "ko", l: "🇰🇷 Korece" },
  { k: "zh", l: "🇹🇼 Çince" }, { k: "hi", l: "🇮🇳 Hintçe" }, { k: "id", l: "🇮🇩 Endonezce" }, { k: "az", l: "🇦🇿 Azerice" },
];

// Basit markdown → HTML (kopyalama için: başlık/kalın/link/liste/tablo/paragraf)
export function mdToHtml(md: string): string {
  const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const inline = (s: string) => esc(s)
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" style="max-width:100%">')
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');
  const lines = md.split("\n");
  const out: string[] = [];
  let list: string[] = []; let table: string[][] = [];
  const flushList = () => { if (list.length) { out.push("<ul>" + list.map((l) => `<li>${inline(l)}</li>`).join("") + "</ul>"); list = []; } };
  const flushTable = () => {
    if (table.length) {
      const [head, ...rows] = table;
      out.push("<table border=\"1\" cellpadding=\"6\" style=\"border-collapse:collapse\"><thead><tr>" + head.map((c) => `<th>${inline(c)}</th>`).join("") + "</tr></thead><tbody>" +
        rows.map((r) => "<tr>" + r.map((c) => `<td>${inline(c)}</td>`).join("") + "</tr>").join("") + "</tbody></table>");
      table = [];
    }
  };
  for (const raw of lines) {
    const line = raw.trimEnd();
    const h = line.match(/^(#{1,4})\s+(.*)/);
    if (h) { flushList(); flushTable(); out.push(`<h${h[1].length}>${inline(h[2])}</h${h[1].length}>`); continue; }
    if (/^\s*[-*]\s+/.test(line)) { flushTable(); list.push(line.replace(/^\s*[-*]\s+/, "")); continue; }
    if (/^\|.*\|$/.test(line)) { flushList(); if (!/^\|[\s:-]+\|$/.test(line.replace(/[^|\s:-]/g, ""))) { const cells = line.slice(1, -1).split("|").map((c) => c.trim()); if (!cells.every((c) => /^:?-+:?$/.test(c))) table.push(cells); } continue; }
    flushList(); flushTable();
    if (line.trim().startsWith(">")) { out.push(`<blockquote style="margin:12px 0;padding:10px 14px;border-left:4px solid #f59e0b;background:#fdf6ec"><p style="margin:4px 0">${inline(line.trim().replace(/^>\s?/, ""))}</p></blockquote>`); continue; }
    if (line.trim()) out.push(`<p>${inline(line)}</p>`);
  }
  flushList(); flushTable();
  return out.join("\n");
}

// Mini markdown önizleme — GERÇEK tablolar + görsellerle
export function MiniMd({ text }: { text: string }) {
  // Inline: görsel → kalın → link (görsel deseni linkten ÖNCE denenir)
  const inline = (s: string) => s.split(/(!\[[^\]]*\]\([^)]+\)|\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g).map((p, j) => {
    const im = p.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (im) return <img key={j} src={im[2]} alt={im[1]} loading="lazy" className="inline-block my-2 rounded-lg max-w-full" style={{ maxHeight: 360 }} />;
    const b = p.match(/^\*\*(.+)\*\*$/); if (b) return <strong key={j}>{b[1]}</strong>;
    const a = p.match(/^\[([^\]]+)\]\(([^)]+)\)$/); if (a) return <a key={j} href={a[2]} target="_blank" rel="noopener noreferrer" className="underline decoration-dotted text-blue-light-700 dark:text-blue-light-500">{a[1]}</a>;
    return p;
  });

  const lines = text.split("\n");
  const blocks: React.ReactNode[] = [];
  let i = 0, key = 0;
  while (i < lines.length) {
    const line = lines[i];
    // Tablo bloğu: ardışık |...| satırları gerçek <table> olur
    if (/^\s*\|.*\|\s*$/.test(line)) {
      const rows: string[][] = [];
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) {
        const cells = lines[i].trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
        if (!cells.every((c) => /^:?-{2,}:?$/.test(c) || c === "")) rows.push(cells);
        i++;
      }
      if (rows.length > 0) {
        const [head, ...body] = rows;
        blocks.push(
          <div key={key++} className="overflow-x-auto my-3">
            <table className="text-xs border-collapse w-full">
              <thead><tr>{head.map((c, j) => <th key={j} className="border px-2.5 py-1.5 text-left font-semibold" style={{ borderColor: "rgba(0,0,0,0.12)", background: "rgba(0,0,0,0.035)" }}>{inline(c)}</th>)}</tr></thead>
              <tbody>{body.map((r, ri) => (
                <tr key={ri}>{r.map((c, j) => <td key={j} className="border px-2.5 py-1.5" style={{ borderColor: "rgba(0,0,0,0.08)" }}>{inline(c)}</td>)}</tr>
              ))}</tbody>
            </table>
          </div>
        );
      }
      continue;
    }
    // Bağımsız görsel satırı
    const img = line.trim().match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (img) { blocks.push(<img key={key++} src={img[2]} alt={img[1]} loading="lazy" className="my-3 rounded-lg max-w-full" style={{ maxHeight: 420 }} />); i++; continue; }
    const h = line.match(/^(#{1,4})\s+(.*)/);
    if (h) { const L = h[1].length; blocks.push(<div key={key++} className={L === 1 ? "text-lg font-bold mt-3" : L === 2 ? "text-base font-bold mt-3" : "text-sm font-semibold mt-2"}>{inline(h[2])}</div>); i++; continue; }
    if (/^\s*[-*]\s+/.test(line)) { blocks.push(<div key={key++} className="pl-4">• {inline(line.replace(/^\s*[-*]\s+/, ""))}</div>); i++; continue; }
    // Kavram/uzman kutusu (alıntı bloğu) → Healthline tarzı kutu
    if (line.trim().startsWith(">")) {
      const box: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith(">")) { box.push(lines[i].trim().replace(/^>\s?/, "")); i++; }
      blocks.push(
        <div key={key++} className="my-3 rounded-lg border-l-4 px-3.5 py-2.5" style={{ borderColor: "#f59e0b", background: "rgba(245,158,11,0.07)" }}>
          {box.filter(Boolean).map((b, j) => <p key={j}>{inline(b)}</p>)}
        </div>
      );
      continue;
    }
    if (line.trim()) blocks.push(<p key={key++}>{inline(line)}</p>);
    i++;
  }
  return <div className="text-sm leading-relaxed space-y-2">{blocks}</div>;
}

export default function ContentTranslate() {
  const { data: domains } = useListDomains();
  const [activeDomainId, setActiveDomainId] = useActiveDomain(domains);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [pageId, setPageId] = useState<number | null>(null);
  const [targetLang, setTargetLang] = useState("de");
  const [pageSearch, setPageSearch] = useState("");
  const [pageLangFilter, setPageLangFilter] = useState<string>("all"); // çevrilecek sayfalar dil filtresi
  const [listLangFilter, setListLangFilter] = useState<string>("all"); // çeviri listesi hedef dil filtresi
  const [showAllTargetLangs, setShowAllTargetLangs] = useState(false);

  const { data: pagesResp } = useQuery({
    queryKey: ["pages-list", activeDomainId],
    enabled: !!activeDomainId,
    queryFn: async (): Promise<{ pages: PageItem[] }> => {
      try { const r = await fetch(`/api/domains/${activeDomainId}/pages-list`); return r.ok ? await r.json() : { pages: [] }; } catch { return { pages: [] }; }
    },
  });
  const filteredPages = useMemo(() => {
    const q = pageSearch.toLowerCase();
    return (pagesResp?.pages ?? [])
      .filter((p) => pageLangFilter === "all" || (p.language ?? "?") === pageLangFilter)
      .filter((p) => !q || (p.title ?? "").toLowerCase().includes(q) || p.url.toLowerCase().includes(q)).slice(0, 200);
  }, [pagesResp, pageSearch, pageLangFilter]);

  // Domain'in TÜM çevirileri (liste görünümü)
  const { data: allTranslations } = useQuery({
    queryKey: ["content-translations", activeDomainId],
    enabled: !!activeDomainId,
    queryFn: async (): Promise<{ translations: TranslationListItem[] }> => {
      try { const r = await fetch(`/api/domains/${activeDomainId}/content-translations`); return r.ok ? await r.json() : { translations: [] }; } catch { return { translations: [] }; }
    },
  });

  const run = useMutation({
    mutationFn: async (): Promise<{ started: boolean }> => {
      const r = await fetch(`/api/domains/${activeDomainId}/content-translate`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ pageId, targetLang }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? "Çeviri başlatılamadı");
      return r.json();
    },
    onSuccess: () => {
      toast({ title: "🌍 Çeviri başlatıldı", description: "Arka planda çevriliyor — bitince kartlarda görünecek." });
      queryClient.invalidateQueries({ queryKey: ["translation-runs", activeDomainId] });
    },
  });

  // Aktif çeviri koşuları — kartlarda "çevriliyor" + bitince BİLDİRİM
  type TRun = { key: string; pageId: number; targetLang: string; pageTitle: string; running: boolean; error: string | null };
  const { data: runsData } = useQuery({
    queryKey: ["translation-runs", activeDomainId],
    enabled: !!activeDomainId,
    refetchInterval: (q) => ((q.state.data as { runs?: TRun[] } | undefined)?.runs ?? []).some((r) => r.running) ? 4000 : 15000,
    queryFn: async (): Promise<{ runs: TRun[] }> => {
      try { const r = await fetch(`/api/domains/${activeDomainId}/translation-runs`); return r.ok ? await r.json() : { runs: [] }; } catch { return { runs: [] }; }
    },
  });
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const activeRuns = (runsData?.runs ?? []).filter((r) => r.running);
  const failedRuns = (runsData?.runs ?? []).filter((r) => !r.running && r.error && !dismissed.has(r.key));
  // Biten koşuları yakala → bildirim + liste tazele
  const prevRunning = useRef<Set<string>>(new Set());
  useEffect(() => {
    const now = new Set(activeRuns.map((r) => r.key));
    for (const key of prevRunning.current) {
      if (!now.has(key)) {
        const fin = (runsData?.runs ?? []).find((r) => r.key === key);
        if (fin?.error) toast({ title: "❌ Çeviri başarısız", description: `${fin.pageTitle} → ${fin.targetLang.toUpperCase()}: ${fin.error}`, variant: "destructive" });
        else toast({ title: "✅ Çeviri tamamlandı", description: `${fin?.pageTitle ?? "İçerik"} → ${(fin?.targetLang ?? "").toUpperCase()} hazır — karttan detayına bak.` });
        queryClient.invalidateQueries({ queryKey: ["content-translations", activeDomainId] });
      }
    }
    prevRunning.current = now;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runsData]);

  const delTranslation = async (id: number) => {
    if (!window.confirm("Bu çeviri silinsin mi?")) return;
    await fetch(`/api/content-translations/${id}`, { method: "DELETE" });
    queryClient.invalidateQueries({ queryKey: ["content-translations", activeDomainId] });
  };


  const selectedPage = (pagesResp?.pages ?? []).find((p) => p.id === pageId);
  const targetLabel = TARGET_LANGS.find((t) => t.k === targetLang)?.l ?? targetLang;

  return (
    <div className="px-4 sm:px-6 py-6 w-full">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="flex items-center gap-2 text-sm" style={{ color: C.purple }}><Languages className="w-4 h-4" /> İçerik Çevirisi</div>
          <h1 className="text-2xl font-bold text-foreground mt-0.5">Çeviri Ajanı</h1>
          <p className="text-sm text-muted-foreground">İçeriği hedef pazarın arama alışkanlıklarına göre yeniden oluşturur.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] px-2.5 py-1 rounded-lg font-medium" style={activeRuns.length > 0 ? { background: `${C.cyan}18`, color: "#b12220" } : { background: "rgba(34,197,94,0.1)", color: "#039855" }}>
            {activeRuns.length > 0 ? `⏳ ${activeRuns.length} çeviri sürüyor` : "● Hazır"}
          </span>
        </div>
      </div>

      {/* ── ÇEVİRİ AJANI PANELİ — solda kaynak, sağda hedef pazar + başlat ── */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(300px,380px)_1fr] gap-4 mb-4 lg:items-start">
        {/* Sol: Kaynak İçerikler */}
        <Card><CardContent className="p-4">
          <div className="text-sm font-semibold mb-2">📄 Kaynak İçerikler</div>
          <div className="relative mb-2">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={pageSearch} onChange={(e) => setPageSearch(e.target.value)} placeholder="Başlık veya URL ile ara…"
              className="w-full text-xs pl-8 pr-3 py-2.5 rounded-xl border outline-none bg-white border-black/10 text-gray-800 placeholder:text-gray-400 dark:bg-gray-800 dark:border-gray-700 dark:text-white/90 dark:placeholder:text-gray-500" />
          </div>
          {(() => {
            const langs = [...new Set((pagesResp?.pages ?? []).map((p) => p.language ?? "?"))].sort();
            return langs.length > 1 ? (
              <div className="flex items-center gap-1 flex-wrap mb-2">
                {["all", ...langs].map((l) => (
                  <button key={l} onClick={() => setPageLangFilter(l)} className="text-[10px] px-2 py-0.5 rounded-full transition"
                    style={pageLangFilter === l ? { background: `${C.purple}22`, color: "#d42b29" } : { background: "rgba(0,0,0,0.05)", color: "#a3a3a0" }}>
                    {l === "all" ? "Tümü" : l.toUpperCase()}
                  </button>
                ))}
              </div>
            ) : null;
          })()}
          <div className="max-h-[440px] overflow-y-auto space-y-2 pr-1">
            {filteredPages.map((p) => {
              const active = pageId === p.id;
              return (
                <button key={p.id} onClick={() => setPageId(p.id)} className={`w-full text-left px-3 py-2.5 rounded-xl transition border relative ${active ? "dark:bg-white/[0.04]" : "bg-white border-black/10 dark:bg-gray-800 dark:border-gray-700"}`}
                  style={active ? { background: "rgba(139,92,246,0.06)", borderColor: "#e83e3c", boxShadow: "0 0 0 1px #e83e3c inset" } : undefined}>
                  <div className="flex items-start gap-2">
                    <span className="mt-0.5 w-3.5 h-3.5 rounded-full border shrink-0 flex items-center justify-center" style={active ? { borderColor: "#e83e3c", background: "#e83e3c" } : { borderColor: "rgba(0,0,0,0.25)" }}>
                      {active && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-semibold truncate" style={active ? { color: "#d42b29" } : undefined}>{p.title || shortUrl(p.url)}</div>
                      <div className="text-[10px] text-muted-foreground truncate">{shortUrl(p.url)}</div>
                      <div className="flex items-center gap-4 mt-1.5 text-[10px]">
                        <span><span className="text-muted-foreground">Dil</span><br /><b>{(p.language ?? "?").toUpperCase()}</b></span>
                        {p.primaryKeyword && <span className="min-w-0"><span className="text-muted-foreground">Odak Kelime</span><br /><b className="truncate block max-w-[160px]">{p.primaryKeyword}</b></span>}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
            {filteredPages.length === 0 && <div className="text-xs text-muted-foreground p-2">Sayfa bulunamadı.</div>}
          </div>
        </CardContent></Card>

        {/* Sağ: Hedef Pazar + Başlat — içerik kadar yükseklik (boşluk bırakmaz) */}
        <Card><CardContent className="p-5">
          <div className="text-sm font-semibold mb-2">🌍 Hedef Pazar</div>
          <div className="flex flex-wrap gap-2 mb-1">
            {(showAllTargetLangs ? TARGET_LANGS : TARGET_LANGS.filter((m) => ["tr", "en", "de", "fr", "es"].includes(m.k))).map((m) => (
              <AgentOption key={m.k} check active={targetLang === m.k} onClick={() => setTargetLang(m.k)} label={m.l} sub={showAllTargetLangs ? undefined : " "} />
            ))}
            {!showAllTargetLangs && (
              <AgentOption active={false} onClick={() => setShowAllTargetLangs(true)} label="＋ Tüm Dilleri Gör" sub={`${TARGET_LANGS.length} dil desteklenir`} />
            )}
          </div>

          <div className="flex items-end justify-between gap-3 flex-wrap pt-5 border-t mt-5" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
            <div className="text-[11px] text-muted-foreground">
              {selectedPage
                ? <><b className="text-foreground">{selectedPage.title || shortUrl(selectedPage.url)}</b> → {targetLabel}</>
                : "Soldan bir kaynak içerik seç."}
            </div>
            <button disabled={!pageId || run.isPending} onClick={async () => { if (await confirmCost("translate")) run.mutate(); }}
              className="px-5 py-3 rounded-xl text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2"
              style={{ background: "linear-gradient(135deg, #d42b29, #e83e3c)" }}>
              {run.isPending ? <><Loader2 className="w-4 h-4 animate-spin" /> Başlatılıyor…</> : <>✨ Çeviriyi Başlat</>}
            </button>
          </div>
        </CardContent></Card>
      </div>

      {run.isError && <Card className="mb-4" style={{ borderColor: "#f0443866" }}><CardContent className="p-3 text-sm text-red-500">{(run.error as Error)?.message}</CardContent></Card>}
      {/* ── AKTİF ÇEVİRİLER — arka planda süren koşular kart olarak ── */}
      {activeRuns.length > 0 && (
        <div className="space-y-2 mb-4">
          {activeRuns.map((r) => (
            <Card key={r.key} style={{ borderColor: "rgba(6,182,212,0.35)", background: "rgba(6,182,212,0.04)" }}>
              <CardContent className="p-4 flex items-center gap-3">
                <Loader2 className="w-5 h-5 animate-spin shrink-0" style={{ color: C.cyan }} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">{r.pageTitle}</div>
                  <div className="text-[11px] text-muted-foreground">→ {TARGET_LANGS.find((x) => x.k === r.targetLang)?.l ?? r.targetLang.toUpperCase()} · kelimeler yerelleştiriliyor → pazar verileri doğrulanıyor → GEO/SEO çeviri yazılıyor… (1-2 dk, bitince bildirim gelir)</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      {failedRuns.map((r) => (
        <Card key={r.key} className="mb-3" style={{ borderColor: "#f0443866" }}>
          <CardContent className="p-3 text-sm text-red-500 flex items-center justify-between gap-3">
            <span className="min-w-0">❌ <b>{r.pageTitle}</b> → {r.targetLang.toUpperCase()}: {r.error}</span>
            <Button size="sm" variant="outline" className="text-xs shrink-0" onClick={() => setDismissed((s) => new Set([...s, r.key]))}>Kapat</Button>
          </CardContent>
        </Card>
      ))}

            {/* ── ÇEVRİLEN SAYFALAR — domain'in tüm çevirileri alt alta, detaylı ── */}
      {(allTranslations?.translations?.length ?? 0) > 0 && (
        <div className="mt-6">
          <div className="flex items-center gap-2 mb-3">
            <Languages className="w-4 h-4" style={{ color: C.cyan }} />
            <h2 className="font-semibold text-base">Çevrilen Sayfalar</h2>
            <span className="text-xs text-muted-foreground">({allTranslations!.translations.length} çeviri)</span>
            <div className="flex items-center gap-1 flex-wrap ml-auto">
              {["all", ...new Set(allTranslations!.translations.map((x) => x.targetLang))].map((l) => (
                <button key={l} onClick={() => setListLangFilter(l)} className="text-[10px] px-2 py-0.5 rounded-full transition"
                  style={listLangFilter === l ? { background: `${C.purple}22`, color: "#d42b29" } : { background: "rgba(0,0,0,0.05)", color: "#a3a3a0" }}>
                  {l === "all" ? "Tümü" : (TARGET_LANGS.find((x) => x.k === l)?.l ?? l.toUpperCase())}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-3">
            {allTranslations!.translations.filter((x) => listLangFilter === "all" || x.targetLang === listLangFilter).map((t) => {
              const langInfo = TARGET_LANGS.find((x) => x.k === t.targetLang);
              const isActive = pageId === t.pageId && targetLang === t.targetLang;
              return (
                <Card key={t.id} style={isActive ? { borderColor: `${C.purple}66`, background: `${C.purple}05` } : undefined}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      {t.thumb && <Link href={`/translation/${t.id}`}><img src={t.thumb} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} className="rounded-lg object-cover shrink-0 cursor-pointer" style={{ width: 96, height: 60 }} /></Link>}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="text-[11px] px-2 py-0.5 rounded-full font-medium" style={{ background: `${C.purple}15`, color: "#d42b29" }}>
                            {(t.sourceLang ?? "?").toUpperCase()} → {langInfo?.l ?? t.targetLang.toUpperCase()}
                          </span>
                          {t.clusterName && <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: "rgba(0,0,0,0.05)", color: "#70706c" }}>{t.clusterName}</span>}
                          <span className="text-[11px] text-muted-foreground">{new Date(t.runAt).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" })}</span>
                        </div>
                        <div className="text-sm">
                          <span className="text-muted-foreground">{t.sourceTitle || shortUrl(t.sourceUrl)}</span>
                          <span className="mx-1.5 text-muted-foreground">→</span>
                          <span className="font-semibold">{t.translatedTitle || "(başlıksız)"}</span>
                        </div>
                        {/* Kelime eşleme özeti */}
                        {t.keywords.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {t.keywords.map((k, i) => (
                              <span key={i} className="text-[11px] px-2 py-0.5 rounded" style={{ background: "rgba(6,182,212,0.08)", color: "#b12220" }} title={`kaynak: ${k.source}`}>
                                {k.source} → <b>{k.chosen}</b>{k.volume > 0 && <span className="opacity-70"> · {fmt(k.volume)}/ay</span>}
                              </span>
                            ))}
                          </div>
                        )}
                        {/* İstatistik satırı */}
                        <div className="flex items-center gap-3 flex-wrap mt-2 text-[11px] text-muted-foreground">
                          {typeof t.score === "number" && (
                            <span className="font-semibold px-1.5 py-0.5 rounded" style={{ background: t.score >= 80 ? "rgba(34,197,94,0.12)" : t.score >= 60 ? "rgba(245,158,11,0.14)" : "rgba(239,68,68,0.12)", color: t.score >= 80 ? "#039855" : t.score >= 60 ? "#b54708" : "#d92d20" }} title="İçerik skoru (kelime yerleşimi + yapı)">📊 {t.score}/100</span>
                          )}
                          <span>📄 {t.wordCount ? `${fmt(t.wordCount)} kelime` : `${fmt(t.contentLength)} karakter`}</span>
                          <span style={{ color: t.linksMapped > 0 ? "#039855" : undefined }}>🔗 {t.linksMapped} link eşlendi</span>
                          {t.linksRemoved > 0 && <span className="text-warning-700 dark:text-warning-400">⚠️ {t.linksRemoved} link karşılıksız (çıkarıldı)</span>}
                          <span>🤖 {t.geoNoteCount} GEO taktiği</span>
                          {t.completeness && <span style={{ color: t.completeness.translatedHeadings >= t.completeness.sourceHeadings ? "#039855" : "#b54708" }}>📑 {t.completeness.translatedHeadings}/{t.completeness.sourceHeadings} başlık</span>}
                          <a href={t.sourceUrl} target="_blank" rel="noopener noreferrer" className="underline decoration-dotted hover:text-cyan-600">{shortUrl(t.sourceUrl)}</a>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Link href={`/translation/${t.id}`}><Button size="sm" variant="outline" className="gap-1.5 text-xs"><Languages className="w-3.5 h-3.5" /> Detay →</Button></Link>
                        <Button size="sm" variant="ghost" className="text-xs text-red-400 hover:text-red-500" onClick={() => delTranslation(t.id)}>🗑</Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
